import { useId, type SVGProps } from "react";

export const CheckCircleIcon: React.FC<
  SVGProps<SVGSVGElement> & {
    size?: number;
    bgColor?: string;
    checkColor?: string;
  }
> = ({
  size = 12,
  bgColor = "#31C859",
  checkColor = "#FFFFFF",
  className,
  ...props
}) => {
  const scale = size / 9;

  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 9 9"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
      {...props}
    >
      <circle cx={4.125} cy={4.125} r={4.125} fill={bgColor} />
      <path
        d="M5.95793 2.75L3.43709 5.5L2.29126 4.25"
        stroke={checkColor}
        strokeWidth={0.458333 * scale}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
};

export const ErrorCircleIcon: React.FC<
  SVGProps<SVGSVGElement> & {
    size?: number;
    bgColor?: string;
    iconColor?: string;
  }
> = ({
  size = 12,
  bgColor = "#FF3B30",
  iconColor = "#FFFFFF",
  className,
  ...props
}) => {
  const scale = size / 9;

  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 9 9"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
      {...props}
    >
      <circle cx={4.125} cy={4.125} r={4.125} fill={bgColor} />
      <path
        d="M5.25 2.875L2.75 5.375M2.75 2.875L5.25 5.375"
        stroke={iconColor}
        strokeWidth={0.5 * scale}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
};

export const WarningCircleIcon: React.FC<
  SVGProps<SVGSVGElement> & {
    size?: number;
    bgColor?: string;
    iconColor?: string;
  }
> = ({
  size = 12,
  bgColor = "#F28D19",
  iconColor = "#FFFFFF",
  className,
  ...props
}) => {
  const scale = size / 9;

  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 9 9"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
      {...props}
    >
      <circle cx={4.125} cy={4.125} r={4.125} fill={bgColor} />
      <path
        d="M2.25 4.125H6.25"
        stroke={iconColor}
        strokeWidth={0.5 * scale}
        strokeLinecap="round"
      />
    </svg>
  );
};

export const MoreCircleIcon: React.FC<
  SVGProps<SVGSVGElement> & {
    size?: number;
    bgColor?: string;
    iconColor?: string;
  }
> = ({ size = 12, bgColor = "#A3A3A3", iconColor = "#FFFFFF", className }) => {
  const scale = size / 9;

  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 9 9"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
    >
      <circle cx={4.125} cy={4.125} r={4.125} fill={bgColor} />

      {/* Dot 1 */}
      <path
        d="M2.25 4.375C2.38807 4.375 2.5 4.26307 2.5 4.125C2.5 3.98693 2.38807 3.875 2.25 3.875C2.11193 3.875 2 3.98693 2 4.125C2 4.26307 2.11193 4.375 2.25 4.375Z"
        stroke={iconColor}
        strokeWidth={0.5 * scale}
        strokeLinecap="round"
        strokeLinejoin="round"
      />

      {/* Dot 2 */}
      <path
        d="M4 4.375C4.13807 4.375 4.25 4.26307 4.25 4.125C4.25 3.98693 4.13807 3.875 4 3.875C3.86193 3.875 3.75 3.98693 3.75 4.125C3.75 4.26307 3.86193 4.375 4 4.375Z"
        stroke={iconColor}
        strokeWidth={0.5 * scale}
        strokeLinecap="round"
        strokeLinejoin="round"
      />

      {/* Dot 3 */}
      <path
        d="M5.75 4.375C5.88807 4.375 6 4.26307 6 4.125C6 3.98693 5.88807 3.875 5.75 3.875C5.61193 3.875 5.5 3.98693 5.5 4.125C5.5 4.26307 5.61193 4.375 5.75 4.375Z"
        stroke={iconColor}
        strokeWidth={0.5 * scale}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
};

export const WhatsAppStrokeIcon: React.FC<
  SVGProps<SVGSVGElement> & {
    size?: number;
    colors?: [string, string];
  }
> = ({ size = 36, colors = ["#2A2D32", "#2A2D32"], ...props }) => {
  const id = useId();
  const gradientId = `gradient-${id}`;
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 18 18"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      {...props}
    >
      <path
        d="M13.0613 10.7865C12.8385 10.6748 11.7428 10.1363 11.5388 10.0613C11.334 9.98703 11.1855 9.95028 11.0363 10.1738C10.8885 10.3965 10.461 10.8983 10.3312 11.0468C10.2015 11.196 10.071 11.214 9.84825 11.103C9.6255 10.9905 8.907 10.7558 8.05575 9.99678C7.3935 9.40578 6.94575 8.67603 6.816 8.45253C6.68625 8.22978 6.8025 8.10903 6.9135 7.99803C7.014 7.89828 7.137 7.73778 7.248 7.60803C7.35975 7.47753 7.3965 7.38453 7.4715 7.23528C7.54575 7.08678 7.509 6.95703 7.45275 6.84528C7.3965 6.73353 6.951 5.63628 6.76575 5.19003C6.58425 4.75578 6.4005 4.81503 6.264 4.80753C6.13425 4.80153 5.98575 4.80003 5.8365 4.80003C5.688 4.80003 5.4465 4.85553 5.2425 5.07903C5.0385 5.30178 4.4625 5.84103 4.4625 6.93828C4.4625 8.03478 5.26125 9.09453 5.37225 9.24378C5.484 9.39228 6.94425 11.6438 9.18 12.609C9.71175 12.8385 10.1265 12.9758 10.4505 13.0778C10.9845 13.248 11.4705 13.224 11.8538 13.1663C12.282 13.1025 13.1723 12.627 13.3583 12.1065C13.5443 11.586 13.5443 11.1398 13.488 11.0468C13.4325 10.9538 13.2848 10.8983 13.0613 10.7865ZM8.99475 16.3388H8.99175C7.66408 16.3389 6.36081 15.9819 5.2185 15.3053L4.94775 15.1448L2.142 15.8813L2.8905 13.1453L2.71425 12.8648C1.97208 11.683 1.57946 10.3153 1.58175 8.91978C1.5825 4.83228 4.90875 1.50678 8.99775 1.50678C10.9777 1.50678 12.8393 2.27928 14.2388 3.68028C14.9293 4.36774 15.4766 5.18533 15.849 6.08571C16.2214 6.9861 16.4116 7.95141 16.4085 8.92578C16.4063 13.0133 13.0807 16.3388 8.99475 16.3388ZM15.3045 2.61603C14.4781 1.78422 13.4949 1.12468 12.4118 0.675631C11.3287 0.226579 10.1673 -0.00305719 8.99475 3.07332e-05C4.0785 3.07332e-05 0.07725 4.00128 0.075 8.91903C0.075 10.491 0.48525 12.0255 1.266 13.3778L0 18L4.72875 16.7595C6.03644 17.472 7.50183 17.8453 8.991 17.8455H8.99475C13.9102 17.8455 17.9123 13.8443 17.9145 8.92578C17.9181 7.75374 17.6893 6.59261 17.2413 5.50956C16.7933 4.42651 16.135 3.44303 15.3045 2.61603Z"
        fill={`url(#${gradientId})`}
      />
      <defs>
        <linearGradient
          id={gradientId}
          x1="0"
          y1="0"
          x2="18"
          y2="18"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor={colors[0]} />
          <stop offset="1" stopColor={colors[1]} />
        </linearGradient>
      </defs>
    </svg>
  );
};

export const MailStrokeIcon: React.FC<
  SVGProps<SVGSVGElement> & {
    size?: number;
    colors?: [string, string];
  }
> = ({
  size = 36,
  colors = ["#2A2D32", "#2A2D32"],
  strokeWidth = 0.7,
  ...props
}) => {
  const id = useId();
  const gradientId = `gradient-${id}`;
  const stroke = strokeWidth != null ? Number(strokeWidth) : 0;
  const pad = stroke / 2;
  const viewBox = stroke
    ? `${-pad} ${-pad} ${18 + stroke} ${13 + stroke}`
    : "0 0 18 13";
  const paint = `url(#${gradientId})`;
  return (
    <svg
      width={size}
      height={size}
      viewBox={viewBox}
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      {...props}
    >
      <path
        d="M17.0345 2.39559L12.0439 7.18048L16.9942 11.475C17.0205 11.39 17.0345 11.2999 17.0345 11.2071L17.0345 2.39559ZM1.632 12.0887C1.68366 12.0983 1.73795 12.1035 1.7931 12.1035H16.1379C16.2027 12.1035 16.2658 12.0965 16.327 12.0834L11.3942 7.80371L10.5064 8.65474C9.83227 9.30176 8.77548 9.32278 8.07678 8.7029L7.02089 7.76781L1.632 12.0887ZM1.7931 0.896552C1.48754 0.896552 1.217 1.04977 1.05502 1.28355L8.67122 8.03212C9.02057 8.34118 9.5494 8.33154 9.88648 8.0076L16.8847 1.29669C16.7245 1.05592 16.4496 0.896556 16.1379 0.896556L1.7931 0.896552ZM0.896552 11.2069C0.896552 11.3067 0.913187 11.4021 0.942955 11.4914L6.33975 7.16446L0.896558 2.34101L0.896552 11.2069ZM17.931 11.2069C17.931 12.1971 17.1282 13 16.1379 13H1.7931C0.802862 13 0 12.1971 0 11.2069V1.7931C0 0.802862 0.802862 0 1.7931 0H16.1379C17.1282 0 17.931 0.802862 17.931 1.7931V11.2069Z"
        fill={paint}
        stroke={stroke ? paint : undefined}
        strokeWidth={stroke || undefined}
        strokeLinejoin={stroke ? "round" : undefined}
      />
      <defs>
        <linearGradient
          id={gradientId}
          x1="0"
          y1="0"
          x2="18"
          y2="13"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor={colors[0]} />
          <stop offset="1" stopColor={colors[1]} />
        </linearGradient>
      </defs>
    </svg>
  );
};

/** Circle + exclamation — error / alert hero mark. Colors follow theme. */
export const ErrorAlertIcon: React.FC<
  SVGProps<SVGSVGElement> & { size?: number; colors?: [string, string] }
> = ({ size = 65, colors = ["#2A2D32", "#2A2D32"], className, ...props }) => {
  const id = useId();
  const paintId = `error-alert-paint-${id}`;
  const paint = `url(#${paintId})`;
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 58 58"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
      aria-hidden
      {...props}
    >
      <path
        d="M28.1826 40.0202C27.9708 40.0202 27.7907 39.9461 27.6425 39.7978C27.4942 39.6495 27.4201 39.4695 27.4201 39.2577V24.2609C27.4201 24.0491 27.4942 23.869 27.6425 23.7207C27.7907 23.5725 27.9708 23.4983 28.1826 23.4983H30.8198C31.0528 23.4983 31.2328 23.5725 31.3599 23.7207C31.5082 23.869 31.5823 24.0491 31.5823 24.2609V39.2577C31.5823 39.4695 31.5082 39.6495 31.3599 39.7978C31.2328 39.9461 31.0528 40.0202 30.8198 40.0202H28.1826ZM28.0555 20.7659C27.8437 20.7659 27.6637 20.6917 27.5154 20.5435C27.3671 20.3952 27.293 20.2151 27.293 20.0033V17.7157C27.293 17.5039 27.3671 17.3238 27.5154 17.1755C27.6637 17.0273 27.8437 16.9531 28.0555 16.9531H30.9468C31.1798 16.9531 31.3705 17.0273 31.5188 17.1755C31.667 17.3238 31.7412 17.5039 31.7412 17.7157V20.0033C31.7412 20.2151 31.667 20.3952 31.5188 20.5435C31.3705 20.6917 31.1798 20.7659 30.9468 20.7659H28.0555Z"
        fill={paint}
      />
      <path
        d="M28.7977 55.7477C43.6817 55.7477 55.7477 43.6817 55.7477 28.7977C55.7477 13.9136 43.6817 1.84766 28.7977 1.84766C13.9136 1.84766 1.84766 13.9136 1.84766 28.7977C1.84766 43.6817 13.9136 55.7477 28.7977 55.7477Z"
        stroke={paint}
        strokeWidth="3.696"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <defs>
        <linearGradient
          id={paintId}
          x1="1.85"
          y1="28.8"
          x2="55.75"
          y2="28.8"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor={colors[0]} />
          <stop offset="1" stopColor={colors[1]} />
        </linearGradient>
      </defs>
    </svg>
  );
};

/** Info mark on a light-gray disc — cancel confirm (same mark as fund-request). */
export const LimitInfoIcon: React.FC<
  SVGProps<SVGSVGElement> & { size?: number; colors?: [string, string] }
> = ({ size = 60, colors = ["#2A2D32", "#2A2D32"], ...props }) => {
  const id = useId();
  const paintId = `limit-info-paint-${id}`;
  const paint = `url(#${paintId})`;
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 60 60"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden
      {...props}
    >
      <circle cx="30" cy="30" r="30" fill="#EEEEEE" />
      <g transform="translate(14 14)">
        <rect
          x="0.842105"
          y="0.842105"
          width="30.3158"
          height="30.3158"
          rx="15.1579"
          stroke={paint}
          strokeWidth="1.68421"
        />
        <path
          d="M14.9535 23C14.8158 23 14.6987 22.9518 14.6023 22.8554C14.5059 22.759 14.4576 22.6419 14.4576 22.5041V12.7521C14.4576 12.6143 14.5059 12.4972 14.6023 12.4008C14.6987 12.3044 14.8158 12.2562 14.9535 12.2562H16.6684C16.8199 12.2562 16.937 12.3044 17.0196 12.4008C17.116 12.4972 17.1643 12.6143 17.1643 12.7521V22.5041C17.1643 22.6419 17.116 22.759 17.0196 22.8554C16.937 22.9518 16.8199 23 16.6684 23H14.9535ZM14.8709 10.4793C14.7331 10.4793 14.616 10.4311 14.5196 10.3347C14.4232 10.2383 14.375 10.1212 14.375 9.98347V8.49587C14.375 8.35813 14.4232 8.24105 14.5196 8.14463C14.616 8.04821 14.7331 8 14.8709 8H16.751C16.9025 8 17.0265 8.04821 17.1229 8.14463C17.2194 8.24105 17.2676 8.35813 17.2676 8.49587V9.98347C17.2676 10.1212 17.2194 10.2383 17.1229 10.3347C17.0265 10.4311 16.9025 10.4793 16.751 10.4793H14.8709Z"
          fill={paint}
        />
      </g>
      <defs>
        <linearGradient
          id={paintId}
          x1="1"
          y1="16"
          x2="31"
          y2="16"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor={colors[0]} />
          <stop offset="1" stopColor={colors[1]} />
        </linearGradient>
      </defs>
    </svg>
  );
};
